import { Injectable, inject } from '@angular/core';
import type { EchartsConfig, ZoomSliderMode } from 'commons';
import type { DataZoomComponentOption, GridComponentOption, YAXisComponentOption } from 'echarts';
import type { XAXisOption } from 'echarts/types/dist/shared';
import type { DtisZoomEvent } from '../zoomEvent';
import { CHART_COLORS } from './echarts-config.types';
import { GridBuilderService } from './grid-builder.service';

/**
 * Service responsible for building data zoom configurations for ECharts multilane charts.
 */
@Injectable({ providedIn: 'root' })
export class DataZoomBuilderService {
  private readonly gridBuilder = inject(GridBuilderService);

  /**
   * Builds data zoom configurations based on zoom mode
   */
  buildDataZoom(
    grid: GridComponentOption[],
    xAxis: XAXisOption[],
    yAxis: YAXisComponentOption[],
    zoomSliderMode: ZoomSliderMode,
    zoom: DtisZoomEvent[],
    config?: EchartsConfig
  ): DataZoomComponentOption[] {
    const xAxisIndexes = xAxis.map((_, i) => i);

    if (!grid || grid.length === 0) {
      return [];
    }

    switch (zoomSliderMode) {
      case 'lane':
        return this.buildLaneZoom(xAxis, zoom, zoomSliderMode, config);

      case 'global':
        return [
          this.createHorizontalSlider(xAxisIndexes, 0, zoom, zoomSliderMode, config),
          this.createInsideZoom(xAxisIndexes, zoom, config),
          ...this.buildVerticalSliders(grid, yAxis, zoom, zoomSliderMode, config),
        ];

      default:
        return [this.createHorizontalSlider(xAxisIndexes, 0, zoom, zoomSliderMode, config), this.createInsideZoom(xAxisIndexes, zoom, config)];
    }
  }

  /**
   * Creates the base zoom slider styling configuration from config
   */
  private createZoomSliderBrand(config?: EchartsConfig): Partial<DataZoomComponentOption> {
    const handleColor = config?.zoomSliderHandleColor ?? CHART_COLORS.SCHAEFFLER_GREEN;
    const fillerColor = config?.zoomSliderFillerColor ?? 'rgba(0, 137, 61, 0.12)';
    const borderColor = config?.zoomSliderBorderColor ?? 'rgba(100, 100, 100, 0.12)';
    const bgColor = config?.zoomSliderBackgroundColor ?? 'transparent';

    return {
      type: 'slider',
      show: config?.zoomSliderShow ?? true,
      realtime: config?.zoomSliderRealtime ?? true,
      moveOnMouseWheel: config?.zoomSliderMoveOnMouseWheel ?? 'shift',
      preventDefaultMouseMove: false,
      showDataShadow: config?.zoomSliderShowDataShadow ?? false,
      moveHandleStyle: {
        color: handleColor,
      },
      fillerColor,
      borderColor,
      // emphasis: {
      //   moveHandleStyle: {
      //     color: handleColor,
      //   },
      //   handleStyle: {
      //     color: handleColor,
      //   },
      // },
      handleStyle: {
        color: handleColor,
        borderWidth: config?.zoomSliderHandleBorderWidth ?? 0,
      },
      borderRadius: [
        config?.zoomSliderBorderRadius ?? 4,
        config?.zoomSliderBorderRadius ?? 4,
        config?.zoomSliderBorderRadius ?? 4,
        config?.zoomSliderBorderRadius ?? 4,
      ],
      backgroundColor: bgColor,
    };
  }

  private buildLaneZoom(xAxis: XAXisOption[], zoom: DtisZoomEvent[], mode: ZoomSliderMode, config?: EchartsConfig): DataZoomComponentOption[] {
    const sliders = xAxis.map((x, xIndex) => this.createHorizontalSlider([xIndex], x.gridIndex ?? 0, zoom, mode, config));

    const insideZooms = xAxis.map((_, i) => this.createInsideZoom([i], zoom, config));

    return [...sliders, ...insideZooms];
  }

  private buildVerticalSliders(
    grid: GridComponentOption[],
    yAxis: YAXisComponentOption[],
    zoom: DtisZoomEvent[],
    mode: ZoomSliderMode,
    config?: EchartsConfig
  ): DataZoomComponentOption[] {
    return grid
      .filter((_, gridIndex) => this.hasSingleYAxis(gridIndex, yAxis))
      .map((gridItem) => {
        const gridIndex = this.extractGridIndex(gridItem.id);
        const yAxisIndex = yAxis.findIndex((y) => y.gridIndex === gridIndex);
        return this.createVerticalSlider([yAxisIndex], gridIndex, zoom, mode, config);
      });
  }

  private createHorizontalSlider(
    xAxisIndex: number[],
    gridIndex: number,
    zoom: DtisZoomEvent[],
    mode: ZoomSliderMode,
    config?: EchartsConfig
  ): DataZoomComponentOption {
    const id = `zoom_${xAxisIndex}_${gridIndex}`;
    const zoomEntity = zoom.find((z) => z.dataZoomId === id);
    const sliderHeight = config?.zoomSliderHeight ?? 30;

    return {
      ...this.createZoomSliderBrand(config),
      id,
      xAxisIndex,
      start: zoomEntity?.start ?? 0,
      end: zoomEntity?.end ?? 100,
      top: this.calculateSliderPosition(gridIndex, mode, sliderHeight, config),
      height: sliderHeight,
      zoomOnMouseWheel: false,
      brushSelect: config?.zoomSliderBrushSelect ?? true,
      throttle: config?.zoomSliderThrottle ?? 100,
    };
  }

  private createInsideZoom(xAxisIndex: number[], zoom: DtisZoomEvent[], config?: EchartsConfig): DataZoomComponentOption {
    const id = `insideZoom_${xAxisIndex.join('_')}`;
    const zoomEntity = zoom.find((z) => z.dataZoomId === id);

    return {
      id,
      type: 'inside',
      xAxisIndex,
      zoomOnMouseWheel: config?.zoomSliderZoomOnMouseWheel ?? false,
      moveOnMouseMove: config?.zoomSliderMoveOnMouseMove ?? true,
      start: zoomEntity?.start ?? 0,
      end: zoomEntity?.end ?? 100,
      throttle: config?.zoomSliderThrottle ?? 100,
    };
  }

  private createVerticalSlider(
    yAxisIndex: number[],
    gridIndex: number,
    zoom: DtisZoomEvent[],
    mode: ZoomSliderMode,
    config?: EchartsConfig
  ): DataZoomComponentOption {
    const id = `vertical_zoom_${yAxisIndex}_${gridIndex}`;
    const zoomEntity = zoom.find((z) => z.dataZoomId === id);
    const verticalSliderWidth = config?.zoomSliderVerticalWidth ?? 30;

    return {
      ...this.createZoomSliderBrand(config),
      id,
      type: 'slider',
      yAxisIndex,
      orient: 'vertical',
      left: '1%',
      // right: config?.zoomSliderVerticalRight ?? 0,
      top: this.gridBuilder.calculateGridTopPosition(gridIndex, mode, config),
      width: verticalSliderWidth,
      height: config?.gridHeight ?? 180,
      show: mode === 'global',
      start: zoomEntity?.start ?? 0,
      end: zoomEntity?.end ?? 100,
    };
  }

  private calculateSliderPosition(gridIndex: number, mode: ZoomSliderMode, _sliderHeight: number, config?: EchartsConfig): number {
    if (mode === 'global') {
      return config?.zoomSliderGlobalTop ?? 0;
    }
    const gridHeight = config?.gridHeight ?? 180;
    const gridGap = config?.gridGap ?? 40;
    return this.gridBuilder.calculateGridTopPosition(gridIndex, mode, config) + gridHeight + gridGap;
  }

  private hasSingleYAxis(gridIndex: number, yAxis: YAXisComponentOption[]): boolean {
    return yAxis.filter((y) => y.gridIndex === gridIndex).length <= 1;
  }

  private extractGridIndex(gridId: string | number | undefined): number {
    return Number(String(gridId).replace('grid-', ''));
  }
}
